import type { SearchableOption } from "./types";

type RawOption = { label?: unknown; value?: unknown; desc?: unknown };

// In particular, we declare the types of `value` in options to unconditionally be strings
function scalar(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return undefined;
}

function requestPath(url: string, query: string): string {
  const target = new URL(url, document.baseURI);
  target.searchParams.set("q", query);
  return target.pathname + target.search;
}

export async function fetchOptions(url: string, query: string): Promise<SearchableOption[]> {
  const response = await fetch(requestPath(url, query), {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) throw new Error(`searchable select fetch failed: ${response.status} from ${new URL(url, document.baseURI).pathname}`);

  const data: unknown = await response.json();
  if (!Array.isArray(data)) throw new Error("searchable select expected an array, but did not receive one");

  return (data as RawOption[]).flatMap((item) => {
    if (item === null || typeof item !== "object") return [];
    const value = scalar(item.value);
    const label = scalar(item.label);
    if (value === undefined || !label) return []; // There's not a lot we can do without a human-facing label

    const option: SearchableOption = { value, label };
    const desc = scalar(item.desc);
    if (desc) option.desc = desc;
    return [option];
  });
}
